/**
 * Converts a GeoJSON polygon geometry to Leaflet-compatible [lat, lng] coordinate arrays.
 * GeoJSON stores coordinates as [longitude, latitude]; Leaflet expects [latitude, longitude].
 *
 * @param {Object} polygon - GeoJSON geometry object (Polygon or MultiPolygon)
 * @returns {Array} - Leaflet positions array suitable for react-leaflet <Polygon positions={...} />
 *                    For Polygon: [[lat, lng], ...]
 *                    For MultiPolygon: [[[lat, lng], ...], ...]
 */
export function geoJsonToLeaflet(polygon) {
  if (!polygon?.coordinates?.length) {
    return [];
  }

  if (polygon.type === "MultiPolygon") {
    return polygon.coordinates.map((multiPolygon) =>
      (multiPolygon[0] || []).map(([lng, lat]) => [lat, lng])
    );
  }

  // Polygon
  return (polygon.coordinates[0] || []).map(([lng, lat]) => [lat, lng]);
}

/**
 * Returns the center point of a Leaflet positions array.
 * Falls back to the geographic center of India if no valid positions are supplied.
 *
 * @param {Array} positions - Leaflet positions array (output of geoJsonToLeaflet)
 * @returns {[number, number]} - [lat, lng] center coordinate
 */
export function getPolygonCenter(positions) {
  const INDIA_CENTER = [20.5937, 78.9629];

  if (!Array.isArray(positions) || positions.length === 0) {
    return INDIA_CENTER;
  }

  // MultiPolygon: positions[0] is an array of [lat, lng] pairs
  if (Array.isArray(positions[0][0])) {
    return positions[0][0];
  }

  // Polygon: positions[0] is [lat, lng]
  return positions[0];
}

/**
 * Converts a GeoJSON Point coordinate pair [lng, lat] to a Leaflet [lat, lng] position.
 *
 * @param {[number, number]} coordinates - GeoJSON coordinates [longitude, latitude]
 * @returns {[number, number] | null} - Leaflet [lat, lng] or null if invalid
 */
export function geoPointToLeaflet(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return null;
  }
  const [lng, lat] = coordinates;
  return [lat, lng];
}

/**
 * Ray-casting point-in-polygon test for GeoJSON coordinates.
 * Both the point and the polygon ring use [longitude, latitude] order (GeoJSON).
 *
 * @param {[number, number]} coordinates - GeoJSON point [longitude, latitude]
 * @param {Object} polygon - GeoJSON Polygon or MultiPolygon geometry object
 * @returns {boolean}
 */
export function isPointInPolygon(coordinates, polygon) {
  if (!coordinates || !polygon?.coordinates?.length) return false;

  const [px, py] = coordinates; // [lng, lat]

  function inRing(ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const intersect =
        yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  if (polygon.type === "MultiPolygon") {
    return polygon.coordinates.some((poly) => inRing(poly[0] || []));
  }
  // Polygon
  return inRing(polygon.coordinates[0] || []);
}
